import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Contact } from "@/components/Contact";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, Shield, Paintbrush, SprayCan } from "lucide-react";
import fassadeHero from "@/assets/fassade-hero.jpg";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import facadeRef1 from "@/assets/facade-ref-1.jpg";
import facadeRef2 from "@/assets/facade-ref-2.webp";
import facadeRef3 from "@/assets/facade-ref-3.png";

const pillars = [
  {
    icon: Shield,
    number: "01",
    title: "Energetische Sanierung (WDVS)",
    text: "Wir machen Ihr Haus fit für die Zukunft. Durch modernste Dämmsysteme reduzieren wir Wärmebrücken und optimieren Ihre Energiebilanz.",
    details: [
      "Systeme: EPS, Mineralwolle oder Hochleistungs-Resol",
      "Vorteil: Spürbare Heizkostenersparnis und staatliche Förderfähigkeit"
    ]
  },
  {
    icon: Paintbrush,
    number: "02",
    title: "Schutz & Instandsetzung",
    text: "Risse im Putz oder abplatzende Farbe sind Warnsignale. Wir sanieren fachgerecht, bevor Folgeschäden an der Bausubstanz entstehen.",
    details: [
      "Leistungen: Riss- & Putzreparatur, Sockelarbeiten, Altbausanierung",
      "Schutz: Hochwertige Imprägnieranstriche gegen Schlagregen"
    ]
  },
  {
    icon: SprayCan,
    number: "03",
    title: "Optik & Langzeitschutz (Algenstopp)",
    text: "Ein sauberer Anstrich wertet Ihre Immobilie sofort auf. Wir nutzen modernste Silikonharzfarben mit Filmschutz.",
    details: [
      "Effekt: Aktiver Schutz vor Algen- und Pilzbefall",
      "Design: Individuelle Farb- & Gestaltungskonzepte nach Ihrem Geschmack"
    ]
  }
];

const materialTable = [
  { material: "EPS (Styropor)", wls: "032 – 035", besonderheit: "Wirtschaftlich, leicht & bewährt." },
  { material: "Mineralwolle", wls: "035", besonderheit: "Nicht brennbar, exzellenter Schallschutz." },
  { material: "Resol-Dämmung", wls: "022", besonderheit: "Maximale Dämmung bei geringster Dicke." },
];

const projects = [
  { image: facadeRef1, caption: "Fassadensanierung in Herford-Diebrock" },
  { image: facadeRef2, caption: "WDVS-Projekt in Hiddenhausen" },
  { image: facadeRef3, caption: "Fassadenanstrich in Enger" },
];

const faqs = [
  {
    question: "Wann ist der beste Zeitpunkt für einen Fassadenanstrich?",
    answer: "Idealerweise zwischen Frühjahr und Herbst bei trockener Witterung und Temperaturen über 5°C."
  },
  {
    question: "Wie erkenne ich, ob meine Dämmung noch effizient ist?",
    answer: "Achten Sie auf kalte Innenwände oder Schimmelbildung in den Ecken. Wir führen gerne einen professionellen Fassaden-Check vor Ort durch."
  },
  {
    question: "Gibt es Förderungen für WDVS?",
    answer: "Ja, bei energetischen Sanierungen können oft KfW-Mittel oder steuerliche Vorteile genutzt werden. Wir unterstützen Sie bei der technischen Dokumentation."
  }
];

const Fassadensanierung = () => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Fassadensanierung & WDVS",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Malerfachbetrieb Stein GmbH und Co. KG",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Mindener Str. 183",
        "addressLocality": "Herford",
        "postalCode": "32049",
        "addressRegion": "NRW",
        "addressCountry": "DE"
      },
      "telephone": "+49-5221-3832130"
    },
    "areaServed": [
      { "@type": "City", "name": "Herford" },
      { "@type": "City", "name": "Hiddenhausen" },
      { "@type": "City", "name": "Löhne" }
    ],
    "description": "Profi-Fassadensanierung in Herford. WDVS, Algenstopp, Rissreparatur und energetische Beratung."
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer },
    })),
  };

  const structuredData = { "@context": "https://schema.org", "@graph": [serviceSchema, faqSchema] };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Fassadensanierung in Herford: Schutz für Ihr Zuhause"
        description="Fassadensanierung Herford: WDVS, Algenstopp & Rissreparatur. Weniger Heizkosten, längere Lebensdauer. Kostenloser Fassaden-Check ☎ 05221 3832130"
        keywords="Fassadensanierung Herford, WDVS Herford, Wärmedämmung Herford, Fassadenanstrich Herford, Algenstopp Fassade, Rissreparatur Fassade"
        canonical="https://malerfachbetrieb-stein.de/fassadensanierung-herford"
        structuredData={structuredData}
      />
      <Navigation />
      <WhatsAppButton />
      
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
          <div className="absolute inset-0">
            <img src={fassadeHero} alt="Fassadensanierung WDVS Herford – Malerfachbetrieb Stein" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-foreground/70" />
          </div>
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 right-10 w-72 h-72 border border-background/5 rotate-45" />
            <div className="absolute bottom-10 left-20 w-96 h-96 border border-background/5 rounded-full" />
          </div>
          <div className="container mx-auto relative z-10">
            <div className="max-w-3xl">
              <span className="inline-block text-sm font-semibold tracking-wider uppercase text-background/50 mb-4">
                Fassadensanierung & WDVS
              </span>
              <h1 className="font-caslon text-3xl md:text-4xl lg:text-5xl lg:text-6xl font-bold text-background mb-6 leading-tight">
                Ihre Fassade in Meisterhand: Werterhalt & Energieeffizienz
              </h1>
              <p className="text-lg md:text-xl text-background/80 mb-10 leading-relaxed max-w-2xl">
                Als Fachbetrieb für energetische Sanierung im Kreis Herford schützen wir Ihre 
                Immobilie nachhaltig vor Witterung und senken massiv Ihre Heizkosten.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-background text-foreground hover:bg-background/90 font-semibold px-10 py-7 text-base rounded-2xl transition-all duration-500 hover:scale-105 group"
                  onClick={() => document.getElementById('kontakt-fassade')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Jetzt kostenloses Angebot anfordern
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
                <a href="tel:+4952213832130">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-background text-background hover:bg-background/10 font-semibold px-10 py-7 text-base rounded-2xl">
                    <Phone className="mr-2 h-5 w-5" />
                    05221 / 3832130
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Einleitung */}
        <section className="py-16 md:py-24 lg:py-32 bg-background">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground mb-8">
              Warum eine professionelle Fassadensanierung der beste Invest ist
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Eine Fassade ist weit mehr als nur die optische Visitenkarte Ihres Hauses. Sie ist die 
              Schutzhülle, die Feuchtigkeit fernhält und wertvolle Wärme im Inneren speichert. Ob 
              klassischer Fassadenanstrich, Putzreparatur oder ein hocheffizientes 
              Wärmedämmverbundsystem (WDVS) – wir bieten Ihnen die passende Lösung für Ihr Objekt 
              in Herford, Hiddenhausen oder Löhne.
            </p>
          </div>
        </section>

        {/* 3 Säulen */}
        <section className="py-16 md:py-24 lg:py-32 bg-muted/30">
          <div className="container mx-auto">
            <div className="max-w-3xl mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase text-muted-foreground mb-4 block">
                Unsere Expertise
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground">
                Die 3 Säulen unserer Fassadenarbeit
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-px bg-foreground/10 rounded-3xl overflow-hidden">
              {pillars.map((pillar, index) => {
                const Icon = pillar.icon;
                return (
                  <div key={index} className="bg-background p-10 flex flex-col gap-6">
                    <div className="flex items-center justify-between">
                      <div className="w-14 h-14 rounded-2xl bg-foreground flex items-center justify-center">
                        <Icon className="w-7 h-7 text-background" />
                      </div>
                      <span className="text-5xl font-bold text-muted-foreground/20 font-caslon">{pillar.number}</span>
                    </div>
                    <h3 className="font-caslon text-xl font-bold text-foreground">{pillar.title}</h3>
                    <p className="text-muted-foreground leading-relaxed text-sm">{pillar.text}</p>
                    <ul className="space-y-2 mt-auto">
                      {pillar.details.map((detail, i) => (
                        <li key={i} className="text-sm text-foreground font-medium flex items-start gap-2">
                          <span className="text-foreground mt-1">•</span>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Technik-Check Dämmstoffe */}
        <section className="py-16 md:py-24 lg:py-32 bg-foreground text-background">
          <div className="container mx-auto">
            <div className="max-w-3xl mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase text-background/50 mb-4 block">
                Technik-Check
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold mb-6">
                Welches Dämmsystem passt zu Ihrem Haus?
              </h2>
              <p className="text-lg text-background/70 leading-relaxed">
                Nicht jede Wand ist gleich. Wir beraten Sie herstellerunabhängig zu den physikalischen 
                Eigenschaften der gängigsten Dämmplatten:
              </p>
            </div>

            <div className="bg-background/10 rounded-3xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background/20">
                    <th className="text-left py-5 px-8 font-semibold text-background">Material</th>
                    <th className="text-left py-5 px-8 font-semibold text-background">Wärmeleitstufe (WLS)</th>
                    <th className="text-left py-5 px-8 font-semibold text-background">Besonderheit</th>
                  </tr>
                </thead>
                <tbody>
                  {materialTable.map((row, i) => (
                    <tr key={i} className="border-b border-background/10 last:border-b-0">
                      <td className="py-5 px-8 font-medium text-background">{row.material}</td>
                      <td className="py-5 px-8 text-background/70 font-mono">{row.wls}</td>
                      <td className="py-5 px-8 text-background/70">{row.besonderheit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Lokale Referenzen */}
        <section className="py-16 md:py-24 lg:py-32 bg-background">
          <div className="container mx-auto">
            <div className="max-w-3xl mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase text-muted-foreground mb-4 block">
                Unsere Arbeit
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground mb-4">
                Fassaden-Projekte aus dem Kreis Herford
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Vom Einfamilienhaus in Herford-Diebrock bis zur Gewerbeimmobilie in Elverdissen – 
                wir kennen die regionalen Anforderungen an Wetterschutz und Werterhalt.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {projects.map((project, index) => (
                <div key={index} className="group relative rounded-2xl overflow-hidden aspect-[4/3]">
                  <img 
                    src={project.image} 
                    alt={project.caption}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <span className="text-sm font-semibold text-background">{project.caption}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 lg:py-32 bg-muted/30">
          <div className="container mx-auto max-w-3xl">
            <div className="text-center mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase text-muted-foreground mb-4 block">
                Häufige Fragen
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground">
                Häufige Fragen zur Fassadensanierung
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`} 
                  className="bg-background border border-foreground/10 rounded-2xl px-6"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-6">
                    <span className="font-semibold pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA + Kontakt */}
        <section id="kontakt-fassade" className="py-16 md:py-24 lg:py-32 bg-foreground">
          <div className="container mx-auto text-center max-w-3xl">
            <h2 className="font-caslon text-3xl md:text-4xl font-bold text-background mb-6">
              Starten Sie jetzt Ihr Sanierungsprojekt
            </h2>
            <p className="text-lg text-background/80 mb-10 leading-relaxed">
              Rufen Sie uns an oder nutzen Sie unser Kontaktformular. Wir besichtigen Ihr Objekt 
              in Herford kurzfristig und erstellen Ihnen ein transparentes Festpreis-Angebot.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+4952213832130">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-background text-background hover:bg-background/10 font-semibold px-10 py-7 text-base rounded-2xl">
                  <Phone className="mr-2 h-5 w-5" />
                  05221 / 3832130
                </Button>
              </a>
              <Button 
                size="lg" 
                className="bg-background text-foreground hover:bg-background/90 font-semibold px-10 py-7 text-base rounded-2xl"
                onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Jetzt Termin vereinbaren
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* Kontaktformular */}
        <Contact />
      </main>
      
      <Footer />
    </div>
  );
};

export default Fassadensanierung;
